import React, { useState, useEffect } from 'react';
import { X, CreditCard, Building2, AlertCircle } from 'lucide-react';
import './StripePaymentMethodModal.scss';

const StripePaymentMethodModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  clientSecret,
  stripePublishableKey 
}) => {
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentType, setPaymentType] = useState('card'); // 'card' or 'bank'
  const [succeeded, setSucceeded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (isOpen && window.Stripe && stripePublishableKey) {
      try {
        const stripeInstance = window.Stripe(stripePublishableKey);
        setStripe(stripeInstance);
        setError(null); // Clear any previous errors
      } catch (err) {
        console.error('Error initializing Stripe:', err);
        setError('Failed to initialize Stripe. Please refresh the page and try again.');
      }
    }
  }, [isOpen, stripePublishableKey]);

  useEffect(() => {
    if (stripe && clientSecret && !elements) {
      console.log('Creating Stripe Elements with clientSecret:', clientSecret);
      
      const appearance = {
        theme: 'stripe',
        variables: {
          colorPrimary: '#4CAF50',
          colorBackground: '#ffffff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          fontFamily: 'system-ui, sans-serif',
          borderRadius: '4px',
        }
      };

      try {
        // Add a small delay to ensure Stripe is fully loaded
        setTimeout(() => {
          const elementsInstance = stripe.elements({
            clientSecret,
            appearance
          });

          setElements(elementsInstance);
          setError(null); // Clear any previous errors
        }, 100);
      } catch (error) {
        console.error('Error creating Stripe Elements:', error);
        setError('Failed to initialize payment form. Please close this modal and try again.');
      }
    }
  }, [stripe, clientSecret, elements]);

  useEffect(() => {
    if (elements && !succeeded) {
      const paymentElement = elements.create('payment', {
        layout: 'tabs',
        defaultValues: {
          billingDetails: {
            email: '',
            phone: '',
            address: {
              country: 'US'
            }
          }
        }
      });

      paymentElement.mount('#payment-element');

      return () => {
        paymentElement.destroy();
      };
    }
  }, [elements, succeeded]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment form not ready. Please wait a moment and try again.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: confirmError } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        console.error('Stripe confirmation error:', confirmError);
        
        // Provide more specific error messages
        if (confirmError.type === 'card_error') {
          setError(confirmError.message);
        } else if (confirmError.type === 'validation_error') {
          setError('Please check your payment information and try again.');
        } else if (confirmError.code === 'setup_intent_authentication_failure') {
          setError('Payment method authentication failed. Please try again.');
        } else {
          setError(`Payment setup failed: ${confirmError.message}`);
        }
        setProcessing(false);
      } else {
        // Payment method was successfully attached
        setSucceeded(true);
        setProcessing(false);
        setRetryCount(0); // Reset retry count on success
        
        // Notify parent component
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Unexpected error during payment setup:', err);
      
      // Handle network errors and other issues
      if (err.name === 'NetworkError' || err.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again or contact support if the problem persists.');
      }
      
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setProcessing(false);
    setSucceeded(false);
    setElements(null);
    setRetryCount(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="stripe-payment-modal-overlay">
      <div className="stripe-payment-modal">
        <div className="modal-header">
          <h2>Add Payment Method</h2>
          <button className="close-button" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {!stripe || !elements ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading payment form...</p>
            </div>
          ) : succeeded ? (
            <div className="success-state">
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="32" fill="#4CAF50"/>
                  <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Payment Method Added!</h3>
              <p>Your payment method has been successfully added.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="payment-info">
                <p className="info-text">
                  Add a payment method to enable manual funding for your company wallet.
                </p>
              </div>

              <div id="payment-element" className="payment-element">
                {/* Stripe Elements will be mounted here */}
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-button" 
                  onClick={handleClose}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={processing || !stripe || !elements}
                >
                  {processing ? (
                    <>
                      <div className="button-spinner"></div>
                      Processing...
                    </>
                  ) : (
                    'Add Payment Method'
                  )}
                </button>
              </div>

              <div className="security-note">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 0L2 3V7.5C2 11.5 8 16 8 16C8 16 14 11.5 14 7.5V3L8 0Z" fill="#6B7280"/>
                </svg>
                <span>Your payment information is securely processed by Stripe</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripePaymentMethodModal;